import { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { LastLocationProvider as LastLocationProviderDeprecated } from 'react-router-last-location';
import { LastPageProvider } from 'fe-design-base/organisms/BackLink/LastPageContext';
import BrowserHistory from 'initializers/BrowserHistory';

import {
  getHiringV2ReleaseEnabled,
  getRolloutEnabled,
} from 'selectors/session';

import BottomDrawerRoot from 'features/bottomDrawers';
import Success from 'features/signUp/components/Success/Success';
import { ROUTES } from 'features/signUp/constants';

import GlobalToast from 'components/GlobalToast/GlobalToast';
import Loader from 'components/Loader';
import SierraChat from 'components/SierraChat';

import { browserHistory } from 'util/router';

import CommandBarRouter from './CommandBarRouter';

// ==========================================================
// Lazy Component Imports by Feature
// ==========================================================

// ----------------------
// Core & Miscellaneous
// ----------------------
const LazyRefreshComponent = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "refresh" */ '../features/refreshComponent'
  )
);

const LazyNavLogoRefreshComponent = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "home" */ 'features/refreshComponent/NavLogoRefreshComponent'
  )
);

const LazySettingsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "settings" */ '../features/settings/SettingsView'
  )
);

const LazyHelpView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "help" */ '../features/help/HelpView'
  )
);

const LazyLocationsMatchView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "locations__match_view" */ 'features/locations/MatchView'
  )
);

// ----------------------
// Authentication & Onboarding
// ----------------------
const LazySignInView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "authentication__sign_in" */ '../features/authentication/SignIn'
  )
);

const LazySignUpRoot = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "signup" */ '../features/signUp/Root'
  )
);

const LazyOnboardingQuestionsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "signup__onboarding_questions" */ '../features/signUp/components/OnboardingQuestionsView'
  )
);

const LazyEmployeeChoiceStep = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "signup__employee_choice_step" */ '../features/signUp/components/EmployeeChoiceStep'
  )
);

const LazyEmployeeGetAppView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "signup__employee_get_app_view" */ '../features/signUp/components/EmployeeGetAppView'
  )
);

// ----------------------
// Integrations
// ----------------------
const LazyCloverSignUpView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "clover__sign_up" */ '../features/signUp/clover/SignUpView'
  )
);

const LazyEmbeddedCloverSignUpView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "clover__embedded__sign_up" */ '../features/signUp/clover/embedded/SignUpView'
  )
);

const LazyLightspeedSignUpView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "lightspeed__sign_up" */ '../features/signUp/lightspeed/SignUpView'
  )
);

const LazyLightspeedLocationSelectView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "lightspeed__location_select" */ '../features/Lightspeed/TimeClockLocationSelect'
  )
);

const LazyLightspeedTimeclockView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "lightspeed__timeclock" */ '../features/Lightspeed/TimeclockApp'
  )
);

// ----------------------
// Team & Employee Management
// ----------------------
const LazyTeamView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "team" */ '../features/teamView'
  )
);

const LazyEmployeeProfileView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "team__profile" */ '../features/employeeProfile'
  )
);

const LazyImportEmployeesView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "team__import" */ '../features/importEmployees/ImportEmployeesView'
  )
);

const LazyImportRolesView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "roles__import" */ '../features/importRoles/ImportRolesView'
  )
);

const LazyDepartmentsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "departments" */ '../features/departments/DepartmentsView'
  )
);

const LazyAcknowledgedView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "team_announcements__acknowledged" */ '../features/teamAnnouncements/AcknowledgedView'
  )
);

// ----------------------
// User Documents
// ----------------------
const LazyPayStatementsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "user_documents__paystubs" */ '../features/user_documents/PayStatementsView'
  )
);

const LazyTaxDocumentsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "user_documents__tax_documents" */ '../features/user_documents/TaxDocumentsView'
  )
);

// ----------------------
// Employee Onboarding
// ----------------------
const LazyNewHireOnboardingView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "employee_onboarding__new_hire" */ '../features/employeeOnboarding/newHire/NewHireOnboardingView'
  )
);

const LazyPacketRoot = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "employee_onboarding__packet" */ '../features/employeeOnboarding/packet/Root'
  )
);

const LazySingleDocumentPacketRoot = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "employee_onboarding__single_document_packet" */ '../features/employeeOnboarding/packet/singleDocument/Root'
  )
);

const LazyDocumentsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "employee_onboarding__documents" */ '../features/employeeOnboarding/documents/DocumentsView'
  )
);

const LazyUploadCustomDocument = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "employee_onboarding__documents" */ '../features/employeeOnboarding/newHire/customDocumentEditor/UploadCustomDocument'
  )
);

const LazyEditCustomDocumentEditor = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "employee_onboarding__documents" */ '../features/employeeOnboarding/newHire/customDocumentEditor/EditCustomDocumentEditor'
  )
);

// ----------------------
// Time Clock & Scheduling
// ----------------------

const LazyTimeClockView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "web_timeclock" */ '../features/timeClock/TimeClockView'
  )
);

const LazyHealthAnswersReportView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "timeclock__health_answers_view" */ '../features/timeClock/HealthAnswersReportView'
  )
);

const LazyHealthQuestionsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "timeclock__health_questions_view" */ '../features/timeClock/HealthQuestionsView'
  )
);

const LazyHubTimeClock = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hub_time_clock" */ '../features/hubTimeClock'
  )
);

const LazyShopifyShimForHubTimeClockView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "shopify_time_clock" */ 'features/shopifyTimeClock/ShopifyShimForHubTimeClockView'
  )
);

const LazyScheduleBuilderView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "schedule_builder" */ '../features/scheduleBuilder/ScheduleBuilderView'
  )
);

const LazyMyAvailabilityView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "my_availability" */ '../features/MyAvailability'
  )
);

const LazyTeamAvailabilityView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "team_availability" */ '../features/TeamAvailability'
  )
);

// ----------------------
// Time Off & PTO
// ----------------------
const LazyTimeOffHistoryView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "time_off__history" */ '../features/timeOff/TimeOffHistoryView'
  )
);

const LazyTimeOffView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "time_off" */ '../features/timeOff/TimeOffView'
  )
);

const LazyPTOPolicyNewStepForm = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "pto_policy__new_step_form" */ '../features/ptoPolicy/PTOPolicyStepForm'
  )
);

const LazyPTOPolicyEditStepForm = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "pto_policy__edit_step_form" */ '../features/ptoPolicy/PTOPolicyStepForm'
  )
);

// ----------------------
// Timesheets
// ----------------------
const LazyTimesheetsPage = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "timesheets" */ '../features/timesheets/TimesheetsPage/TimesheetsPage'
  )
);

const LazyPayrollSummaryDetailView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "timesheets__payroll_summary_detail" */ '../features/timesheets/PayrollSummariesView/PayrollSummaryDetailView'
  )
);

const LazyPayrollSummariesView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "timesheets__payroll_summaries" */ '../features/timesheets/PayrollSummariesView'
  )
);

const LazyCompanyTimesheetsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "timesheets__company_timesheets" */ '../features/timesheets/CompanyTimesheets'
  )
);

// ----------------------
// Payroll
// ----------------------
const LazyTeamTaxFormsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "team__tax_forms" */ '../features/payroll/TeamTaxFormsView'
  )
);

const LazyGoPaperlessSuccessView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll__go_paperless_success_view" */ '../features/payroll/GoPaperless/Success'
  )
);

const LazyGoPaperlessFailureView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll__go_paperless_failure_view" */ '../features/payroll/GoPaperless/Failure'
  )
);

const LazyPayrollRunStandardView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll__runs" */ '../features/payroll/PayrollRunView/Standard'
  )
);

const LazyPayrollRunOffCycleView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll__runs_off_cycle" */ '../features/payroll/PayrollRunView/OffCycle'
  )
);

const LazyTaxReturnsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll__tax_returns" */ '../features/payroll/TaxReturnsView'
  )
);

const LazyLandingLeadGenViewController = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll__landing_lead_gen_view" */ '../features/payroll/LandingView/LandingLeadGenViewController'
  )
);

const LazyPayrollController = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll" */ '../features/payroll/PayrollController'
  )
);

const LazyPayrollTermsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll__terms" */ '../features/payroll/PayrollTermsView/PayrollTermsView'
  )
);

const LazyEmbeddedController = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll__embedded" */ '../features/payroll/Embedded/EmbeddedController'
  )
);

const LazyTasksDashboardView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll__self_serve_setup_dashboard" */ '../features/payroll/SelfServeSetupView/TasksDashboard/TasksDashboardView'
  )
);

const LazyLinearFlowView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll__self_serve_setup_linear_flow" */ '../features/payroll/SelfServeSetupView/LinearFlow/LinearFlowView'
  )
);

const LazyShiftPayMarketingView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "cash_out__money" */ '../features/shiftPay/ShiftPayMarketingView'
  )
);

// ----------------------
// Dashboard
// ----------------------
const LazyCompanyDashboardView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "dashboard__company" */ '../features/companyDashboard/CompanyDashboard'
  )
);

const LazyLocationDashboardView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "dashboard__location" */ '../features/dashboard/DashboardView'
  )
);

const LazyMyWeekView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "my_week" */ '../features/MyWeek'
  )
);

// ----------------------
// Reports
// ----------------------
const LazyReportsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "reports" */ '../features/reports/ReportsView'
  )
);

const LazyPayrollCustomReportView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "custom_report_view" */ '../features/reports/PayrollReportView/CustomReportTemplate/CustomReportView'
  )
);

// ----------------------
// TaskManager & Referrals
// ----------------------

const LazyManagerLogView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "manager_log" */ '../features/ManagerLog'
  )
);

const LazyTaskManager = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "task_manager" */ '../features/taskManager'
  )
);

const LazyTaskManagerTaskListView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "task_manager_task_list_view" */ '../features/taskManager/pages/v1/components/AddTaskListView'
  )
);

const LazyUserReferralsContainer = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "referrals" */ '../features/referrals/UserReferralsContainer'
  )
);

// ----------------------
// Billing & Plans
// ----------------------

const LazyManagePlanView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "manage_plans" */ '../features/managePlan/ManagePlanView'
  )
);

const LazyDowngradeToBasicTierView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "tiers__downgrade" */ '../features/biller/DowngradeToBasicTierView'
  )
);

// ----------------------
// Admin
// ----------------------
const LazyAdminMapView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "admin__map_view" */ '../features/admin/map/MapView'
  )
);

const LazyAdminView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "admin" */ '../features/admin/AdminView'
  )
);

// ----------------------
// Hiring (V1)
// ----------------------
const LazyHiringDashboardView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring__dashboard" */ '../features/hiring/dashboard/DashboardView'
  )
);

const LazyHiringSignInView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring__sign_in" */ 'features/hiring/signIn/SignInView'
  )
);

const LazyJobPostView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring__new_job_post" */ '../features/hiring/jobPost/JobPost'
  )
);

const LazyJobPostSuccessView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring__job_post_success" */ '../features/hiring/jobPost/JobPostSuccessView/Router'
  )
);

const LazyInterviewsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring__interviews" */
    '../features/hiring/interviews/InterviewsView/InterviewsView'
  )
);

const LazyManageApplicantsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring__manage_applicants_view" */
    '../features/hiring/manageApplicants/ManageApplicantsView'
  )
);

const LazyManageApplicantsRouter = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring__manage_applicants_router" */ '../features/hiring/manageApplicants/Router'
  )
);

const LazyApplicantProfileView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring__jobs_profile" */
    '../features/hiring/applicantProfile/ApplicantProfileView'
  )
);

const LazyCompanyProfileView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring__company_profile_view" */ '../features/hiring/companyProfile/CompanyProfileView'
  )
);

const LazyJobRequestView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring__job_request_view" */ '../features/hiring/jobRequest/JobRequestView'
  )
);

// ----------------------
// Hiring V2
// ----------------------
const LazyHiringV2DashboardView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring_v2__dashboard" */ '../features/hiringV2/Dashboard/index'
  )
);

const LazyHiringV2Onboarding = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring_v2__onboarding" */ '../features/hiringV2/Onboarding/index'
  )
);

const LazyHiringV2Waitlist = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring_v2__waitlist" */ '../features/hiringV2/Onboarding/waitlist'
  )
);

const LazyHiringV2ScheduleInterview = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring_v2__schedule_interview" */ '../features/hiringV2/Interviews/schedule'
  )
);

const LazyHiringV2SetInterviewTimes = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring_v2__set_interview_times" */ '../features/hiringV2/Dashboard/setInterviewTimes'
  )
);

const LazyHiringV2CreateJobRequest = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring_v2__job_description" */ '../features/hiringV2/CreateJobRequest'
  )
);

const LazyHiringV2ReviewCandidatesView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring_v2__review_shortlist" */ 'features/hiringV2/ReviewCandidates/index'
  )
);

const LazyHiringV2ReviewJobPostsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hiring_v2__review_job_posts" */ 'features/hiringV2/ReviewJobPosts/index'
  )
);

const LazyScreeningAgentDemo = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "screening_agent_demo" */ 'features/hiringV2/ScreeningAgent/ScreeningAgentDemo'
  )
);

const LazyScreeningQuestionsPage = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "screening_questions_page" */ '../features/hiringV2/pages/ScreeningQuestionsPage'
  )
);

const LazyShareJobRequest = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "share_job_request" */ '../features/hiringV2/ShareJobRequest'
  )
);

// ----------------------
// Product Growth & Onboarding
// ----------------------
const LazyQuickStartGuide = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "onboarding__quick_start_guide" */ 'features/onboarding/QuickStartGuide'
  )
);

const LazyFocusedQSG = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "onboarding__quick_start_guide_focused_qsg" */ 'features/onboarding/QuickStartGuide/FocusedQSG'
  )
);

const LazyLandingPage = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "onboarding__quick_start_guide_splash" */ 'features/onboarding/LandingPage'
  )
);

const LazyPostSignupMainView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll_onboarding" */ 'features/productGrowth/PostSignup/MainView'
  )
);

const LazyReleasesLanding = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "releases_landing" */ '../features/productGrowth/ReleasesLanding'
  )
);

// ----------------------
// Growth
// ----------------------
const LazyTLWASharingView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "growth__tlwa__sharing_view" */ '../features/Growth/TopLocalWorkplaceAward/SharingView'
  )
);
const LazyTLWAStoriesView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "growth__tlwa__stories_view" */ '../features/Growth/TopLocalWorkplaceAward/StoriesView'
  )
);

const LazyLaunchWeekAwardMobileShare = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "growth__launch_week__award_mobile_share" */ '../features/Growth/LaunchWeek/LaunchWeekAwardMobileShare'
  )
);

// ----------------------
// HR Pro
// ----------------------
const LazyEligibilityCheckView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "hr_pro" */ '../features/hrPro/BackgroundChecks/features/eligibilityCheckView/components/EligibilityCheckView/EligibilityCheckView'
  )
);

// ----------------------
// 401k Benefits
// ----------------------
const LazyRetirementBenefitsView = lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "retirement" */ '../features/benefits/RetirementBenefitsView'
  )
);

// ----------------------
// Feature Flag Wrapper Components
// ----------------------

// Wrapper component for HiringV2Dashboard that handles feature flag check
const HiringV2DashboardRouteComponent = () => {
  const isHiringV2Enabled = useSelector(state =>
    getHiringV2ReleaseEnabled(state)
  );

  return isHiringV2Enabled ? (
    <LazyHiringV2DashboardView />
  ) : (
    <LazyHiringDashboardView />
  );
};

// Wrapper component for HiringV2Onboarding that handles feature flag check
const HiringV2OnboardingRouteComponent = () => {
  const isHiringV2Enabled = useSelector(state =>
    getHiringV2ReleaseEnabled(state)
  );

  return isHiringV2Enabled ? (
    <LazyHiringV2Onboarding />
  ) : (
    <LazyHiringDashboardView />
  );
};

// Wrapper component for HiringV2Waitlist that handles feature flag check
const HiringV2WaitlistRouteComponent = () => {
  const isHiringV2RolloutEnabled = useSelector(state =>
    getRolloutEnabled(state, 'hiring_v2_release')
  );

  return isHiringV2RolloutEnabled ? (
    <LazyHiringV2Waitlist />
  ) : (
    <LazyHiringDashboardView />
  );
};

// Wrapper component for HiringV2SetInterviewTimes that handles feature flag check
const HiringV2SetInterviewTimesRouteComponent = () => {
  const isHiringV2RolloutEnabled = useSelector(state =>
    getRolloutEnabled(state, 'hiring_v2_release')
  );

  return isHiringV2RolloutEnabled ? (
    <LazyHiringV2SetInterviewTimes />
  ) : (
    <LazyHiringDashboardView />
  );
};

// Wrapper component for HiringV2JobDescription that handles feature flag check
const HiringV2JobDescriptionRouteComponent = () => {
  const isHiringV2Enabled = useSelector(state =>
    getHiringV2ReleaseEnabled(state)
  );

  return isHiringV2Enabled ? (
    <LazyHiringV2CreateJobRequest />
  ) : (
    <LazyHiringDashboardView />
  );
};

// Wrapper component for HiringV2ReviewShortlist that handles feature flag check
const HiringV2ReviewShortlistRouteComponent = () => {
  const isHiringV2Enabled = useSelector(state =>
    getHiringV2ReleaseEnabled(state)
  );

  return isHiringV2Enabled ? (
    <LazyHiringV2ReviewCandidatesView />
  ) : (
    <LazyHiringDashboardView />
  );
};

// Wrapper component for HiringV2ReviewJobPosts that handles feature flag check
const HiringV2ReviewJobPostsRouteComponent = () => {
  const isHiringV2Enabled = useSelector(state =>
    getHiringV2ReleaseEnabled(state)
  );

  return isHiringV2Enabled ? (
    <LazyHiringV2ReviewJobPostsView />
  ) : (
    <LazyHiringDashboardView />
  );
};

// Wrapper component for HiringV2ScreeningAgentDemo that handles feature flag check
const HiringV2ScreeningAgentDemoRouteComponent = () => {
  const isHiringV2RolloutEnabled = useSelector(state =>
    getRolloutEnabled(state, 'hiring_v2_release')
  );

  return isHiringV2RolloutEnabled ? (
    <LazyScreeningAgentDemo />
  ) : (
    <LazyHiringDashboardView />
  );
};

// Wrapper component for CompanyTimesheets that handles feature flag check
const CompanyTimesheetsRouteComponent = () => {
  const isCompanyTimesheetsEnabled = useSelector(state =>
    getRolloutEnabled(state, 'fe_react_company_timesheets')
  );

  return isCompanyTimesheetsEnabled ? (
    <LazyCompanyTimesheetsView />
  ) : (
    <LazyRefreshComponent />
  );
};

const ROUTE_STYLE = { height: '100%' };

// ==========================================================
// Routes Definition
// ==========================================================

const Routes = () => (
  <Router history={browserHistory}>
    <BrowserHistory>
      <LastLocationProviderDeprecated>
        <LastPageProvider>
          <BottomDrawerRoot />
          <GlobalToast />
          <SierraChat />
          {/*
          Ensure any new wrappers placed around this top-level div are either fragments,
          or are themselves divs with 100% height
          */}
          <div style={ROUTE_STYLE}>
            <Suspense fallback={<Loader isLoading centered />}>
              <CommandBarRouter />
              <Route
                exact
                path="/benefits/retirement"
                component={LazyRetirementBenefitsView}
              />
              <Route
                exact
                path="/jobs/sign_in"
                component={LazyHiringSignInView}
              />
              <Route
                exact
                path="/accounts/sign-in"
                component={LazySignInView}
              />
              <Route
                exact
                path="/locations/match/list"
                component={LazyLocationsMatchView}
              />
              <Switch>
                <Route exact path="/team" component={LazyTeamView} />
                <Route
                  exact
                  path="/user_documents/tax_documents"
                  component={LazyTaxDocumentsView}
                />
                <Route
                  exact
                  path="/user_documents/pay_statements"
                  component={LazyPayStatementsView}
                />
                <Route
                  exact
                  path="/team/tax_forms"
                  component={LazyTeamTaxFormsView}
                />
                <Route
                  exact
                  path="/team/import"
                  component={LazyImportEmployeesView}
                />
                <Route
                  exact
                  path="/roles/import"
                  component={LazyImportRolesView}
                />
                <Route
                  path="/team/:userId"
                  component={LazyEmployeeProfileView}
                />
              </Switch>
              <Switch>
                <Route exact path="/settings" component={LazySettingsView} />
                <Route path="/settings/:page" component={LazySettingsView} />
              </Switch>
              <Switch>
                <Route
                  path="/reports/payroll/journal_templates/new"
                  component={LazyPayrollCustomReportView}
                />
                <Route
                  path="/reports/payroll/journal_templates/:templateID"
                  component={LazyPayrollCustomReportView}
                />
                <Route exact path="/reports" component={LazyReportsView} />
                <Route path="/reports/:page" component={LazyReportsView} />
              </Switch>
              <Switch>
                <Route
                  exact
                  path="/hiring/dashboard"
                  component={LazyHiringDashboardView}
                />
                <Route
                  exact
                  path="/hiring/dashboard/new"
                  component={LazyHiringDashboardView}
                />
                <Route
                  exact
                  path="/hiring/job_post/new"
                  component={LazyJobPostView}
                />
                <Route
                  exact
                  path="/hiring/job_post/:jobRequestId/edit"
                  component={LazyJobPostView}
                />
                <Route
                  exact
                  path="/hiring/job_post/:jobRequestId/success"
                  component={LazyJobPostSuccessView}
                />
                <Route
                  exact
                  path="/hiring/interviews"
                  component={LazyInterviewsView}
                />
                <Route
                  path="/hiring/:ownerType/:ownerId/manage_applicants/:applicantToken?"
                  component={LazyManageApplicantsView}
                />
                <Route
                  path="/hiring/manage_applicants"
                  component={LazyManageApplicantsRouter}
                />
                <Route
                  exact
                  path="/hiring/v2/dashboard"
                  component={HiringV2DashboardRouteComponent}
                />
                <Route
                  exact
                  path="/hiring/v2/onboarding"
                  component={HiringV2OnboardingRouteComponent}
                />
                <Route
                  exact
                  path="/hiring/v2/waitlist"
                  component={HiringV2WaitlistRouteComponent}
                />
                <Route
                  exact
                  path="/hiring/v2/set_interview_times"
                  component={HiringV2SetInterviewTimesRouteComponent}
                />
                <Route
                  path="/hiring/v2/job_requests/new"
                  component={HiringV2JobDescriptionRouteComponent}
                />
                <Route
                  path="/hiring/v2/review_applicants"
                  component={HiringV2ReviewShortlistRouteComponent}
                />
                <Route
                  path="/hiring/v2/job_posts"
                  component={HiringV2ReviewJobPostsRouteComponent}
                />
                <Route
                  path="/hiring/v2/screening_agent_demo"
                  component={HiringV2ScreeningAgentDemoRouteComponent}
                />
              </Switch>
              <Route
                exact
                path="/jobs/schedule_interview"
                component={LazyHiringV2ScheduleInterview}
              />
              <Route exact path="/task_manager" component={LazyTaskManager} />
              <Route
                exact
                path="/task_manager/:id"
                component={LazyTaskManagerTaskListView}
              />
              <Route
                path="/jobs/profile"
                component={LazyApplicantProfileView}
              />
              <Route path="/cmp/:id" component={LazyCompanyProfileView} />
              <Switch>
                <Route
                  path="/loc/:locationProfileId/job/:jobRequestId/screening_agent/:token"
                  component={LazyScreeningQuestionsPage}
                />
                <Route
                  path="/loc/:locationProfileId/job/:jobRequestId/screening_agent"
                  component={LazyScreeningQuestionsPage}
                />
                <Route
                  path="/loc/:locationProfileId/job/:jobRequestId/share"
                  component={LazyShareJobRequest}
                />
                <Route
                  path="/loc/:locationProfileId/job/:jobRequestId"
                  component={LazyJobRequestView}
                />
              </Switch>
              <Route
                exact
                path="/go_paperless_success"
                component={LazyGoPaperlessSuccessView}
              />
              <Route
                exact
                path="/go_paperless_failure"
                component={LazyGoPaperlessFailureView}
              />
              <Route
                path="/dashboard/company"
                component={LazyCompanyDashboardView}
              />
              <Route
                path="/dashboard/location"
                component={LazyLocationDashboardView}
              />
              <Route exact path="/profile" component={LazyMyWeekView} />
              <Route path="/web_timeclock" component={LazyTimeClockView} />
              <Route path="/schedule" component={LazyScheduleBuilderView} />
              <Switch>
                <Route
                  path="/timesheets/payroll_summary_detail"
                  component={LazyPayrollSummaryDetailView}
                />
                <Route
                  path="/timesheets/payroll_summaries"
                  component={LazyPayrollSummariesView}
                />
                <Route
                  matchRoutes
                  path="/timesheets/daily_review"
                  component={LazyTimesheetsPage}
                />
                <Route
                  matchRoutes
                  path="/timesheets/pay_period_review"
                  component={LazyTimesheetsPage}
                />
                <Route
                  matchRoutes
                  path="/timesheets"
                  component={LazyTimesheetsPage}
                />
              </Switch>
              <Switch>
                <Route
                  exact
                  path="/tiers/biller/downgrade"
                  component={LazyDowngradeToBasicTierView}
                />
              </Switch>
              <Route path="/departments" component={LazyDepartmentsView} />
              <Switch>
                <Route
                  exact
                  path="/time_offs/history"
                  component={LazyTimeOffHistoryView}
                />
                <Route path="/time_offs" component={LazyTimeOffView} />
              </Switch>
              <Switch>
                <Route
                  exact
                  path="/pto_policies/new"
                  component={LazyPTOPolicyNewStepForm}
                />
                <Route
                  exact
                  path="/pto_policies/:policyId/edit"
                  component={LazyPTOPolicyEditStepForm}
                />
              </Switch>
              <Route path="/referrals" component={LazyUserReferralsContainer} />
              <Route
                path="/onboarding/questions"
                component={LazyOnboardingQuestionsView}
              />
              <Route exact path={`${ROUTES.QSG_SPLASH}?confirmation_flow=true`}>
                <Success confirmationFlow />
              </Route>
              <Route path="/onboarding/sign-up" component={LazySignUpRoot} />
              <Route
                path="/onboarding/employee_choice"
                component={LazyEmployeeChoiceStep}
              />
              <Route
                path="/onboarding/employee_get_app"
                component={LazyEmployeeGetAppView}
              />
              <Switch>
                <Route
                  exact
                  path="/clover/embedded"
                  component={LazyEmbeddedCloverSignUpView}
                />
                <Route
                  path="/clover/embedded/:page"
                  component={LazyEmbeddedCloverSignUpView}
                />
              </Switch>
              <Switch>
                <Route
                  exact
                  path="/clover/sign-up"
                  component={LazyCloverSignUpView}
                />
                <Route
                  path="/clover/sign-up/:page"
                  component={LazyCloverSignUpView}
                />
              </Switch>
              <Switch>
                <Route
                  path="/lightspeed/sign-up"
                  component={LazyLightspeedSignUpView}
                />
                <Route
                  path="/lightspeed/sign-up/:page"
                  component={LazyLightspeedSignUpView}
                />
              </Switch>
              <Route
                path="/lightspeed/location_select"
                component={LazyLightspeedLocationSelectView}
              />
              <Route path="/security/authentication_tokens/new">
                <Redirect to="/security/authentication-tokens/new" />
              </Route>
              <Route
                path="/security/authentication-tokens/new"
                component={LazySignInView}
              />
              <Switch>
                <Route
                  path="/hr_center/safety"
                  component={LazyHealthAnswersReportView}
                />

                <Route
                  path="/hr_center/health_questions"
                  component={LazyHealthQuestionsView}
                />
                <Redirect
                  from="/timeclock/health_questions"
                  to="/hr_center/health_questions"
                />
                <Redirect
                  from="/timeclock/health_answers"
                  to="/hr_center/safety"
                />
              </Switch>
              <Route path="/admin_map" component={LazyAdminMapView} />
              <Route path="/admin" component={LazyAdminView} />
              <Route
                path="/hr_center/background_checks"
                component={LazyEligibilityCheckView}
              />
              <Route
                path="/hr_center/hr_pro"
                component={LazyEligibilityCheckView}
              />
              <Route path="/hr_pro">
                <Redirect to="/hr_center/background_checks" />
              </Route>
              <Route exact path="/hr_center">
                <Redirect to="/hr_center/background_checks" />
              </Route>
              <Route path="/money" component={LazyShiftPayMarketingView} />
              {/* Deprecated in favor of /money route */}
              <Route path="/cash_out" component={LazyShiftPayMarketingView} />
              {/* Deprecated in favor of /money route */}
              <Route
                path="/instant-pay"
                component={LazyShiftPayMarketingView}
              />
              <Route path="/authentication/mfa" component={LazySignInView} />
              <Route
                path="/authentication/delegated-verification/validate-code"
                component={LazySignInView}
              />
              {/* Multi-wage route */}
              <Route
                path="/payroll/runs"
                component={LazyPayrollRunStandardView}
              />
              <Route
                path="/payroll/tax_records"
                component={LazyTaxReturnsView}
              />
              <Route
                path="/payroll/get-started"
                component={LazyLandingLeadGenViewController}
              />
              <Route path="/payroll" exact component={LazyPayrollController} />
              <Route
                path="/payroll-terms"
                exact
                component={LazyPayrollTermsView}
              />
              <Route
                path="/payroll/embedded"
                exact
                component={LazyEmbeddedController}
              />
              <Switch>
                <Route
                  path="/payroll/setup/implementation"
                  exact
                  component={LazyTasksDashboardView}
                />
                <Route
                  path="/payroll/setup/start"
                  exact
                  component={LazyPayrollController}
                />
                <Route
                  path="/payroll/setup/:step"
                  exact
                  component={LazyLinearFlowView}
                />
              </Switch>
              <Route
                path="/employee_onboarding/single_document_packet/:packetId"
                component={LazySingleDocumentPacketRoot}
              />
              <Route
                path="/employee_onboarding/packet/:packetId"
                component={LazyPacketRoot}
              />
              <Route
                path="/new_hire_onboarding"
                component={LazyNewHireOnboardingView}
              />
              <Route
                path="/employee_onboarding/custom_documents/new"
                component={LazyUploadCustomDocument}
              />
              <Route
                path="/employee_onboarding/custom_documents/:customDocumentId/edit"
                component={LazyEditCustomDocumentEditor}
              />
              <Route path="/documents" component={LazyDocumentsView} />
              {/* /payroll/off_cycle/:id should always be first the in path array */}
              <Route
                path={['/payroll/off_cycle/:id', '/payroll/off_cycle']}
                component={LazyPayrollRunOffCycleView}
              />
              <Route
                path="/team_announcements/acknowledged"
                component={LazyAcknowledgedView}
              />
              <Route path="/help" component={LazyHelpView} />
              <Route path="/hub_time_clock" component={LazyHubTimeClock} />
              <Route
                path="/pos/shopify/time_clock"
                component={LazyShopifyShimForHubTimeClockView}
              />
              <Route
                exact
                path="/quick_start_guide"
                component={LazyQuickStartGuide}
              />
              <Route
                path="/quick_start_guide/focus"
                component={LazyFocusedQSG}
              />
              <Route
                path="/quick_start_guide/splash"
                component={LazyLandingPage}
              />
              <Route exact path="/releases" component={LazyReleasesLanding} />
              <Route
                path="/payroll_onboarding/:page"
                component={LazyPostSignupMainView}
              />
              <Route
                exact
                path="/manage_plans"
                component={LazyManagePlanView}
              />
              {/* These are placeholders for an empty React component that forces a hard refresh.
            This is to enable route blocking via history.block(). Should be replaced/removed if
            these views are ever ported to React.
            https://github.com/remix-run/history/blob/dev/docs/blocking-transitions.md */}
              <Route exact path="/manager_log" component={LazyManagerLogView} />
              <Route
                exact
                path="/availabilities"
                component={LazyTeamAvailabilityView}
              />
              <Route
                exact
                path="/user/availability"
                component={LazyMyAvailabilityView}
              />
              <Route
                exact
                path="/company_timesheets"
                component={CompanyTimesheetsRouteComponent}
              />
              <Route
                exact
                path="/home"
                component={LazyNavLogoRefreshComponent}
              />
              <Route
                path="/growth/tlwa/stories_view"
                exact
                component={LazyTLWAStoriesView}
              />
              <Route
                path="/growth/tlwa/sharing_view"
                exact
                component={LazyTLWASharingView}
              />
              <Route
                path="/growth/launch_week/award_mobile_share"
                exact
                component={LazyLaunchWeekAwardMobileShare}
              />
              <Route
                path="/hb1/aHR0cHM6Ly9"
                exact
                component={LazyLaunchWeekAwardMobileShare}
              />
              <Route
                path="/lightspeed_timeclock"
                exact
                component={LazyLightspeedTimeclockView}
              />
            </Suspense>
          </div>
        </LastPageProvider>
      </LastLocationProviderDeprecated>
    </BrowserHistory>
  </Router>
);

export default Routes;
